// CommentList.tsx — renders the full comment tree for a post-detail page.
// Consumes a CommentThread object (from useCommentThread) and delegates
// all state mutations back through the thread's callbacks.

import { Fragment } from 'react';
import styled from 'styled-components';
import { Dialog, Button } from '@/ui';
import { useSession } from '@/hooks';
import type { CommentThread } from './useCommentThread';
import type { Comment } from '@/core/models/comment';
import { CommentCard } from './CommentCard';
import { ReplyComposer } from './ReplyComposer';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CommentListProps {
  thread: CommentThread;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  /* Back the flat comment thread with a glass-aware surface: opaque when glass is
     OFF so the animated backdrop can't bleed through the comments (and their nested
     reply indents); transparent when glass is ON to keep the glassy look. */
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const ThreadWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const RepliesSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 54px;
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 4px 54px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.accentCyan};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: color 0.15s, background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const ShowMoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 12px auto;
  padding: 6px 16px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.accentCyan};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: color 0.15s, background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const EmptyState = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 24px 16px;
  margin: 0;
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const DialogBody = styled.p`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0 0 16px;
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export function CommentList({ thread }: CommentListProps) {
  const { currentUser } = useSession();

  // Own-comment detection: compare by id, NOT by handle — a handle can diverge
  // from the account's username, which silently broke ownership detection.
  function isOwn(comment: Comment): boolean {
    return currentUser !== null && comment.authorId === currentUser.id;
  }

  // ─── DRY helper: card + optional inline reply composer ───────────────────
  function cardWithReply(comment: Comment) {
    return (
      <Fragment key={comment.id}>
        <CommentCard
          comment={comment}
          isOwn={isOwn(comment)}
          isEditing={thread.editingId === comment.id}
          onReply={thread.openReply}
          onStartEdit={thread.startEdit}
          onSaveEdit={thread.saveEdit}
          onCancelEdit={thread.cancelEdit}
          onRequestDelete={thread.requestDelete}
        />
        {thread.openReplyId === comment.id && (
          <ReplyComposer
            draftScope={`${comment.postId}:${comment.id}`}
            replyingToHandle={thread.replyTargetHandle}
            onSubmit={thread.submitReply}
            onCancel={thread.cancelReply}
            onDraftChange={thread.setReplyDraft}
          />
        )}
      </Fragment>
    );
  }

  // ─── Empty state ──────────────────────────────────────────────────────────
  if (thread.visibleThreads.length === 0) {
    return <EmptyState>Be the first to comment.</EmptyState>;
  }

  // ─── Thread list ──────────────────────────────────────────────────────────
  return (
    <Root>
      {thread.visibleThreads.map((top) => {
        const replies = thread.repliesFor(top.id);
        const expanded = thread.isExpanded(top.id);

        return (
          <ThreadWrap key={top.id}>
            {/* Top-level comment card + optional inline reply composer */}
            {cardWithReply(top)}

            {/* View / hide replies toggle */}
            {replies.length > 0 && (
              <ToggleBtn
                type="button"
                onClick={() => thread.toggleExpanded(top.id)}
              >
                {expanded
                  ? 'Hide replies'
                  : `View replies (${replies.length})`}
              </ToggleBtn>
            )}

            {/* Expanded nested replies */}
            {expanded && replies.length > 0 && (
              <RepliesSection>
                {replies.map((reply) => cardWithReply(reply))}
              </RepliesSection>
            )}
          </ThreadWrap>
        );
      })}

      {/* Show more */}
      {thread.hiddenCount > 0 && (
        <ShowMoreBtn
          type="button"
          onClick={() => thread.setShowAll(true)}
        >
          Show {thread.hiddenCount} more comments
        </ShowMoreBtn>
      )}

      {/* Delete-confirm dialog */}
      {thread.deleteTargetId !== null && (
        <Dialog
          open
          title="Delete this comment?"
          onClose={thread.cancelDelete}
        >
          <DialogBody>This can&#39;t be undone.</DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={thread.cancelDelete}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void thread.confirmDelete()}>
              Delete
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </Root>
  );
}
